import React, { Fragment, useState, useEffect } from "react";
import { connect } from "react-redux";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import AppButton from "../../Common/Button/Button";
import {
  convertCapabilityName,
  isJsonParseSuccessful,
  isJsonStringifySuccessful,
  validateJSONData,
} from "../../../utils/general";
import { Box } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import FormInput from "../../Common/FormInputs/MuiTextField";
import { selectMenuProps } from "../../../utils/selectMenuProps";

const selectInputSx = {
  height: 45,
  minHeight: 45,
  width: "100%",
  "& .MuiOutlinedInput-notchedOutline": { border: "1px solid rgba(0, 0, 0, 0.42) !important" },
};
const capabilityCardSx = { marginBottom: "15px", backgroundColor: "#f2f2f2" };

const CONFIG = {
  customJson: {
    label: "Custom Capability JSON",
    placeholder: 'e.g., {"capability": "IndoorRoomAirTemp"}',
    rows: 4,
    helpText: "Valid JSON required",
  },
};

const getDiagCapabilitiesMap = (deviceModelsMetadata, productCategory) => {
  const diag = deviceModelsMetadata?.categories?.[productCategory]?.diagCapabilities;
  if (!diag) return {};
  // Canonical metadata shape matches statusCapabilities: { IndoorRoomAirTemp: { capabilityName: "IndoorRoomAirTemp" } }
  if (!Array.isArray(diag) && typeof diag === "object") {
    return diag;
  }
  if (Array.isArray(diag)) {
    return diag.reduce((acc, item) => {
      const name = item?.capability || item?.capabilityName;
      if (name) {
        acc[name] = item;
      }
      return acc;
    }, {});
  }
  return {};
};

const DeviceModelDiagCapability = ({
  productCategory,
  isDeviceModelAddedUsingFileUpload,
  triggerAddNewDeviceModel,
  addDeviceDiagCapabilities,
  editDeviceModel,
  deviceModelData,
  ModelData: { deviceModelsMetadata },
  areAllCustomJsonValidCheck,
}) => {
  const customCapabilityName = "CustomCapability";
  const [customCapabilityCount, setCustomCapabilityCount] = useState(1);
  const [diagCapabilitiesList, setDiagCapabilitiesList] = useState([]);
  const [selectedDiagCapability, setSelectedDiagCapability] = useState("");
  const [addedDiagCapabilities, setAddedDiagCapabilities] = useState([]);
  const [diagCapabilitiesAttributes, setDiagCapabilitiesAttributes] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const areAllCustomJsonValid = (errorsObj) => Object.values(errorsObj).every((error) => error === "");
  const isCustomCapability = (value) => typeof value === "string" && value.startsWith(customCapabilityName);
  const isKnownCapability = (value) =>
    Boolean(getDiagCapabilitiesMap(deviceModelsMetadata, productCategory)?.[value]);
  const isCustomLike = (value) => isCustomCapability(value) || !isKnownCapability(value);

  useEffect(() => {
    if (diagCapabilitiesList.length > 0) {
      setSelectedDiagCapability(diagCapabilitiesList[0]);
    } else {
      setSelectedDiagCapability("");
    }
  }, [diagCapabilitiesList]);

  useEffect(() => {
    const diagMap = getDiagCapabilitiesMap(deviceModelsMetadata, productCategory);
    const attrState = {};

    if (editDeviceModel || isDeviceModelAddedUsingFileUpload) {
      const savedDiagCapabilities = Array.isArray(deviceModelData?.diagCapabilities)
        ? deviceModelData.diagCapabilities
        : [];
      for (const capability of savedDiagCapabilities) {
        const capabilityName = capability?.capability;
        if (!capabilityName) continue;
        if (!diagMap[capabilityName]) {
          attrState[capabilityName] = isJsonStringifySuccessful(capability, { formatted: true });
        } else {
          attrState[capabilityName] = {};
          const attributes = capability.attributes;
          if (attributes) {
            attrState[capabilityName].attributes = {};
            for (const index in attributes) {
              attrState[capabilityName].attributes[index] = {
                key: attributes[index]?.key ?? "",
              };
            }
          }
        }
      }
    }

    Object.keys(diagMap).forEach((capabilityName) => {
      if (attrState[capabilityName]) return;
      attrState[capabilityName] = {};
      const attributes = diagMap[capabilityName]?.attributes;
      if (attributes) {
        attrState[capabilityName].attributes = {};
        attributes.forEach((attribute, index) => {
          attrState[capabilityName].attributes[index] = {
            key: attribute?.key ?? "",
          };
        });
      }
    });
    setDiagCapabilitiesAttributes(attrState);
  }, [productCategory, editDeviceModel, isDeviceModelAddedUsingFileUpload, deviceModelsMetadata, deviceModelData]);

  useEffect(() => {
    const diagMap = getDiagCapabilitiesMap(deviceModelsMetadata, productCategory);
    const capabilities = Object.keys(diagMap).sort();

    if (editDeviceModel || isDeviceModelAddedUsingFileUpload) {
      const savedNames = (Array.isArray(deviceModelData?.diagCapabilities)
        ? deviceModelData.diagCapabilities
        : [])
        .map((item) => item?.capability)
        .filter(Boolean);
      const savedOrdered = [
        ...savedNames.filter((cap) => !isCustomLike(cap)),
        ...savedNames.filter((cap) => isCustomLike(cap)),
      ];
      setAddedDiagCapabilities(savedOrdered);
      setDiagCapabilitiesList([
        customCapabilityName,
        ...capabilities.filter((cap) => cap !== customCapabilityName && !savedNames.includes(cap)),
      ]);
    } else {
      setDiagCapabilitiesList([
        customCapabilityName,
        ...capabilities.filter((cap) => cap !== customCapabilityName),
      ]);
      setAddedDiagCapabilities([]);
    }
  }, [productCategory, editDeviceModel, isDeviceModelAddedUsingFileUpload, deviceModelsMetadata, deviceModelData]);

  useEffect(() => {
    if (!triggerAddNewDeviceModel) return;
    addDeviceDiagCapabilities(getDiagCapabilityData());
    areAllCustomJsonValidCheck(areAllCustomJsonValid(errors), "diag");
  }, [triggerAddNewDeviceModel]);

  function handleAddDiagCapability(addedCapabilityName) {
    if (!addedCapabilityName) return;
    if (addedCapabilityName === customCapabilityName) {
      const nextName = `${customCapabilityName}${customCapabilityCount}`;
      setDiagCapabilitiesAttributes((prev) => ({
        ...prev,
        [nextName]: isJsonStringifySuccessful({}, { formatted: true }),
      }));
      setAddedDiagCapabilities((prevState) => {
        const customs = prevState.filter((cap) => isCustomCapability(cap));
        const nonCustoms = prevState.filter((cap) => !isCustomCapability(cap));
        return [...customs, nextName, ...nonCustoms];
      });
      setCustomCapabilityCount((prev) => prev + 1);
      return;
    }

    setAddedDiagCapabilities((prevState) => [...prevState, addedCapabilityName]);
    setDiagCapabilitiesList((prevState) =>
      prevState.filter((capabilityName) => capabilityName !== selectedDiagCapability),
    );
  }

  function handleRemoveDiagCapability(removedCapabilityName) {
    if (isCustomLike(removedCapabilityName)) {
      setDiagCapabilitiesAttributes((prev) => {
        const { [removedCapabilityName]: _, ...rest } = prev;
        return rest;
      });
      setAddedDiagCapabilities((prevState) =>
        prevState.filter((capabilityName) => capabilityName !== removedCapabilityName),
      );
      setErrors((prev) => {
        const { [removedCapabilityName]: _, ...rest } = prev;
        return rest;
      });
      setTouched((prev) => {
        const { [removedCapabilityName]: _, ...rest } = prev;
        return rest;
      });
      return;
    }

    setAddedDiagCapabilities((prevState) =>
      prevState.filter((capabilityName) => capabilityName !== removedCapabilityName),
    );
    setDiagCapabilitiesList((prevState) => [...prevState, removedCapabilityName]);
  }

  function onChangeCapabilityCustomJson(capabilityName, value) {
    setDiagCapabilitiesAttributes((prev) => ({ ...prev, [capabilityName]: value }));
    const error = validateJSONData(value);
    setErrors((prev) => ({ ...prev, [capabilityName]: error }));
    if (error && error !== "") {
      setTouched((prev) => ({ ...prev, [capabilityName]: "true" }));
    }
  }

  function getDiagCapabilityData() {
    const diagCapabilities = [];
    const diagMap = getDiagCapabilitiesMap(deviceModelsMetadata, productCategory);
    for (const capabilityName of addedDiagCapabilities) {
      if (isCustomCapability(capabilityName) || !diagMap[capabilityName]) {
        const parsed = isJsonParseSuccessful(diagCapabilitiesAttributes[capabilityName], { logError: false });
        if (parsed !== false) {
          diagCapabilities.push(parsed);
        }
        continue;
      }

      const capabilityData = { capability: capabilityName };
      const attributes = diagCapabilitiesAttributes[capabilityName]?.attributes;
      if (attributes) {
        capabilityData.attributes = [];
        for (const index in attributes) {
          capabilityData.attributes.push({ key: attributes[index]?.key });
        }
      }
      diagCapabilities.push(capabilityData);
    }
    return diagCapabilities;
  }

  return (
    <Fragment>
      <Grid2 container spacing={2} sx={{ alignItems: "flex-end", mt: 1.5, mb: 2 }}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <FormInput
            type="select"
            label="Select Capability"
            value={selectedDiagCapability}
            onChange={(e) => setSelectedDiagCapability(e.target.value)}
            options={(diagCapabilitiesList ?? []).map((capabilityName) => ({
              value: capabilityName,
              label: convertCapabilityName(capabilityName),
            }))}
            fullWidth
            size="small"
            sx={selectInputSx}
            MenuProps={selectMenuProps}
            inputProps={{ "aria-label": "Select capability" }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <Box sx={{ height: "1.25rem", mb: 0.75 }} aria-hidden />
          <AppButton
            variant="primary"
            size="medium"
            onClick={() => handleAddDiagCapability(selectedDiagCapability)}
            disabled={!diagCapabilitiesList || diagCapabilitiesList.length === 0}
          >
            Add Capability
          </AppButton>
        </Grid2>
      </Grid2>
      {addedDiagCapabilities.map((capabilityName) => {
        const isCustom = isCustomLike(capabilityName);
        return (
          <Fragment key={`${productCategory}_${capabilityName}`}>
            <Card variant="outlined" sx={capabilityCardSx}>
              <CardContent>
                {isCustom ? (
                  <Grid2 container alignItems="flex-start" spacing={1}>
                    <Grid2 size={10} sx={{ margin: "auto" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {CONFIG.customJson.label}
                      </Typography>
                    </Grid2>
                    <Grid2 size={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleRemoveDiagCapability(capabilityName)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Grid2>
                    <Grid2 size={12}>
                      <FormInput
                        id={capabilityName}
                        name={capabilityName}
                        type="textarea"
                        multiline
                        rows={CONFIG.customJson.rows}
                        fullWidth
                        value={
                          typeof diagCapabilitiesAttributes[capabilityName] === "string"
                            ? diagCapabilitiesAttributes[capabilityName]
                            : ""
                        }
                        onChange={(e) => onChangeCapabilityCustomJson(capabilityName, e.target.value)}
                        placeholder={CONFIG.customJson.placeholder}
                        error={!!errors?.[capabilityName]}
                        touched={touched[capabilityName]}
                        helperText={errors?.[capabilityName] || CONFIG.customJson.helpText}
                        sx={{
                          width: "100%",
                          minWidth: "100%",
                          "& .MuiOutlinedInput-input": {
                            padding: "8.5px 14px !important",
                            height: "130px !important",
                          },
                          "& .MuiOutlinedInput-root": {
                            minHeight: 130,
                            height: "auto",
                            alignItems: "flex-start",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "1px solid rgba(0, 0, 0, 0.42) !important",
                            },
                          },
                        }}
                        slotProps={{
                          htmlInput: {
                            maxLength: 10000,
                          },
                        }}
                      />
                    </Grid2>
                  </Grid2>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="button" sx={{ flex: "1 1 auto", minWidth: 0 }}>
                      {convertCapabilityName(capabilityName)}
                    </Typography>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleRemoveDiagCapability(capabilityName)}
                      sx={{ flexShrink: 0, p: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fragment>
        );
      })}
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  ModelData: state.getModel,
});

export default connect(mapStateToProps)(DeviceModelDiagCapability);
